import { useEffect, useState } from 'react';
import { zxcvbnOptions, zxcvbnAsync } from '@zxcvbn-ts/core';
import * as zxcvbnCommonPackage from '@zxcvbn-ts/language-common';
import * as zxcvbnEnPackage from '@zxcvbn-ts/language-en';
import { matcherPwnedFactory } from '@zxcvbn-ts/matcher-pwned';

// Configure zxcvbn options
const matcherPwned = matcherPwnedFactory(fetch, zxcvbnOptions);
zxcvbnOptions.addMatcher('pwned', matcherPwned);

const options = {
  dictionary: {
    ...zxcvbnCommonPackage.dictionary,
    ...zxcvbnEnPackage.dictionary,
  },
  graphs: zxcvbnCommonPackage.adjacencyGraphs,
  useLevenshteinDistance: true,
  translations: zxcvbnEnPackage.translations,
};
zxcvbnOptions.setOptions(options);

export interface PasswordStrengthResult {
  score: number;
  isCompromised: boolean;
  isStrong: boolean;
  feedback: string;
}

export function usePasswordStrength(password: string): {
  result: PasswordStrengthResult | null;
  checkPasswordStrength: () => Promise<PasswordStrengthResult>;
} {
  const [result, setResult] = useState<PasswordStrengthResult | null>(null);

  // Check password strength immediately when password changes
  useEffect(() => {
    if (password.trim()) {
      checkPasswordStrength();
    } else {
      setResult(null);
    }
  }, [password]);

  const checkPasswordStrength = async (): Promise<PasswordStrengthResult> => {
    if (!password.trim()) {
      const emptyResult = {
        score: 0,
        isCompromised: false,
        isStrong: false,
        feedback: '',
      };
      setResult(emptyResult);
      return emptyResult;
    }

    try {
      const zxcvbnResult = await zxcvbnAsync(password.trim());
      const isCompromised =
        zxcvbnResult.feedback.warning === 'This is a commonly used password';
      const isStrong = zxcvbnResult.score >= 3 && !isCompromised;

      let feedback = '';
      if (isCompromised) {
        feedback =
          'This password has been compromised in a data breach. Please choose a different password.';
      } else if (!isStrong) {
        feedback =
          'Password is not secure enough. Add complexity or use a password generator to improve strength.';
      }

      const strengthResult = {
        score: zxcvbnResult.score,
        isCompromised,
        isStrong,
        feedback,
      };

      setResult(strengthResult);
      return strengthResult;
    } catch (error) {
      console.error('Error checking password strength:', error);
      const errorResult = {
        score: 0,
        isCompromised: false,
        isStrong: false,
        feedback: 'Error checking password strength',
      };
      setResult(errorResult);
      return errorResult;
    }
  };

  return { result, checkPasswordStrength };
}
